import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { CreatorForm } from "@/components/CreatorForm";
import { MarketResearch } from "@/components/MarketResearch";
import { ProgressPanel } from "@/components/ProgressPanel";
import { BookReader } from "@/components/BookReader";
import { BookShowcase } from "@/components/BookShowcase";
import { SharedBook } from "@/components/SharedBook";
import { RecentBooks } from "@/components/RecentBooks";
import { AuthScreen } from "@/components/AuthScreen";
import { PricingModal } from "@/components/PricingModal";
import ResetPasswordScreen from "@/components/PasswordReset";
import { LandingPage } from "@/components/LandingPage";
import { BillingPage } from "@/components/BillingPage";
import { SeriesModal } from "@/components/SeriesModal";
import { SeriesPage } from "@/components/SeriesPage";
import { AdminPanel } from "@/components/AdminPanel";
import { InfoPage } from "@/components/InfoPage";
import { SiteFooter } from "@/components/SiteFooter";
import { MarketingSite, isMarketingPage } from "@/components/MarketingSite";
import { ExportsTray } from "@/components/ExportsTray";
import { NotFound } from "@/components/NotFound";
import { SpendCreditDialog } from "@/components/SpendCreditDialog";
import { PAGES as INFO_PAGES } from "@/lib/siteContent";

const APP_PATHS = ["/", "/payment/success", "/payment/cancel", "/auth/google", "/index.html", "/reset-password"];
const pathPage = () => {
  const p = window.location.pathname.replace(/\/+$/, "") || "/";
  if (APP_PATHS.includes(p)) return null;
  const key = p.slice(1);
  if (key === "faq" || INFO_PAGES[key] || isMarketingPage(key)) return key;
  return "404";
};
const setBookUrl = (id) => window.history.replaceState({}, "", id ? `/?book=${id}` : "/");

function App() {
  const [user, setUser] = useState(undefined); // undefined = checking, null = not authed, object = authed
  const [view, setView] = useState("home");
  const [book, setBook] = useState(null);
  const [shareError, setShareError] = useState(false);
  useEffect(() => {
    if (!shareError || user === undefined) return;
    setShareError(false);
    setTimeout(() => toast.error("This share link is invalid or was turned off by the owner."), 300);
  }, [shareError, user]);
  const [busy, setBusy] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingReason, setPricingReason] = useState("");
  const [paying, setPaying] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("register");
  const [series, setSeries] = useState([]);
  const [seriesModalOpen, setSeriesModalOpen] = useState(false);
  const [activeSeriesId, setActiveSeriesId] = useState(null);
  const [prefillSeriesId, setPrefillSeriesId] = useState("");
  const [seed, setSeed] = useState(null);
  const [infoPage, setInfoPage] = useState(() => { const k = pathPage(); return k && k !== "404" ? k : null; });
  const [notFound, setNotFound] = useState(() => pathPage() === "404");
  const [pricingBookId, setPricingBookId] = useState("");
  const [spendOpen, setSpendOpen] = useState(false);
  const pendingBookRef = useRef(new URLSearchParams(window.location.search).get("share") ? null
    : new URLSearchParams(window.location.search).get("book"));
  const pollRef = useRef(null);
  const submitKeyRef = useRef(null);

  const refreshUser = useCallback(async () => {
    try {
      const u = await api.me();
      setUser(u);
      return u;
    } catch {
      return null;
    }
  }, []);

  const loadLibrary = useCallback(async () => {
    try {
      setLibrary(await api.listBooks());
    } catch (e) {
      /* ignore */
    }
  }, []);

  const loadSeries = useCallback(async () => {
    try {
      setSeries(await api.listSeries());
    } catch (e) {
      /* ignore */
    }
  }, []);

  // ---- Payment return handling ----
  const handlePaymentReturn = useCallback(async () => {
    const path = window.location.pathname;
    if (path.startsWith("/payment/cancel")) {
      toast.info("Checkout canceled.");
      window.history.replaceState({}, "", "/");
      return;
    }
    if (!path.startsWith("/payment/success")) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    window.history.replaceState({}, "", "/");
    if (!sessionId) return;
    setPaying(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const s = await api.paymentStatus(sessionId);
        if (s.payment_status === "paid") {
          const u = await refreshUser();
          loadSeries();
          loadLibrary();
          toast.success(s.founding_bonus ? "Payment successful. Welcome, founding author: a bonus credit was added!"
            : "Payment successful. Your account is updated!");
          setPaying(false);
          if (s.book_id) {
            const b = await openBookRef.current?.(s.book_id);
            const entitled = u?.subscription_active || (u?.credits || 0) > 0;
            if (b?.is_preview && entitled && ["book_credits", "credits_1", "credits_3", "credits_10", "unlimited_monthly", "unlimited_yearly"].includes(s.lookup_key)) {
              setSpendOpen(true);
            }
          }
          return;
        }
        if (["failed", "expired"].includes(s.payment_status)) {
          toast.error("Payment did not complete.");
          setPaying(false);
          return;
        }
      } catch {
        /* keep trying */
      }
      if (attempts >= 12) {
        toast.info("Payment is processing. Your credits will appear shortly.");
        setPaying(false);
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [refreshUser, loadSeries, loadLibrary]);

  // ---- Google OAuth callback ----
  // Custom Google OAuth (own client): browser returns to /auth/google?code=...&state=...
  const handleGoogleCodeCallback = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const savedState = sessionStorage.getItem("g_oauth_state");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    window.history.replaceState({}, "", "/");
    if (!code) {
      await refreshUser();
      return;
    }
    if (savedState && state && savedState !== state) {
      toast.error("Google sign-in failed. Please try again.");
      setUser(null);
      return;
    }
    sessionStorage.removeItem("g_oauth_state");
    try {
      const ref = localStorage.getItem("klyr_ref") || "";
      const u = await api.googleCallback(code, redirectUri, ref);
      localStorage.removeItem("klyr_ref");
      setUser(u);
      toast.success(`Welcome, ${u.name || u.email}`);
    } catch (e) {
      toast.error(apiError(e, "Google sign-in failed."));
      setUser(null);
    }
  }, [refreshUser]);

  // Emergent-managed Google login callback (fallback): #session_id=...
  const handleGoogleCallback = useCallback(async () => {
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    window.history.replaceState({}, "", "/");
    if (!sessionId) {
      await refreshUser();
      return;
    }
    try {
      const ref = localStorage.getItem("klyr_ref") || "";
      const u = await api.googleExchange(sessionId, ref);
      localStorage.removeItem("klyr_ref");
      setUser(u);
      toast.success(`Welcome, ${u.name || u.email}`);
    } catch (e) {
      toast.error(apiError(e, "Google sign-in failed."));
      setUser(null);
    }
  }, [refreshUser]);

  useEffect(() => {
    const init = async () => {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("book") && sp.get("share")) {  // explicit share link: read-only view, no login needed
        try {
          setBook(await api.getBook(sp.get("book"), sp.get("share")));
          setView("shared");  // URL kept so a refresh still shows the shared book
        } catch {
          window.history.replaceState({}, "", "/");
          setShareError(true);  // shown once the final screen (and its Toaster) has mounted
        }
      }
      const ref = sp.get("ref");
      if (ref) localStorage.setItem("klyr_ref", ref);
      if (window.location.pathname === "/auth/google" && new URLSearchParams(window.location.search).get("code")) {
        await handleGoogleCodeCallback();
      } else if (window.location.hash?.includes("session_id=")) {
        await handleGoogleCallback();
      } else {
        const u = await refreshUser();
        if (u === null) setUser(null);
      }
    };
    init();
    return () => clearInterval(pollRef.current);
  }, [handleGoogleCallback, handleGoogleCodeCallback, refreshUser]);

  useEffect(() => {
    if (user) {
      loadLibrary();
      loadSeries();
      handlePaymentReturn();
      if (pendingBookRef.current) {  // refresh / deep link: reopen the book (or its live progress)
        const id = pendingBookRef.current;
        pendingBookRef.current = null;
        openBookRef.current?.(id);
      }
    }
  }, [user, loadLibrary, loadSeries, handlePaymentReturn]);

  const startPolling = useCallback((id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const b = await api.getBook(id);
        setBook(b);
        if (b.status === "done") {
          clearInterval(pollRef.current);
          setBusy(false);
          loadLibrary();
          loadSeries();
          refreshUser();
          toast.success("Your book is ready!");
          // Stay on the compiling screen — it transforms into the "ready" state with an Open button.
          // Auto-run a continuity check so any drift is flagged and waiting in the reader.
          if (!["sudoku", "maze", "coloring", "wordsearch", "journal", "workbook"].includes(b.mode)) {
            api.continuityCheck(id).then(() => pollBusy(id)).catch(() => {});
          }
        } else if (b.status === "error") {
          clearInterval(pollRef.current);
          setBusy(false);
          refreshUser();
          toast.error("Book generation failed. Please try again.");
        }
      } catch (e) {
        /* keep polling */
      }
    }, 1500);
  }, [loadLibrary, loadSeries, refreshUser, user]);

  const openPricing = (reason = "", bookId = "") => {
    setPricingReason(reason);
    setPricingBookId(bookId);
    setPricingOpen(true);
  };

  const startCheckout = async (lookup_key, opts = {}) => {
    try {
      const { checkout_url } = await api.checkout(lookup_key, opts);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Could not start checkout."));
    }
  };

  const handleCreateSeries = async (payload) => {
    try {
      const res = await api.createSeries(payload);
      await loadSeries();
      setSeriesModalOpen(false);
      if (res.needs_payment) {
        toast.info("Taking you to checkout to unlock your series…");
        startCheckout("series_unlock", { series_id: res.series.id });
      } else {
        toast.success("Series created — it's included with your plan.");
      }
    } catch (e) {
      toast.error(apiError(e, "Could not create series."));
    }
  };

  const handleBuySeries = (s) => startCheckout("series_unlock", { series_id: s.id });
  const handleBuyPublish = () => {
    if (!book) return;
    if (book.is_preview && (user?.credits || 0) > 0) setSpendOpen(true);  // a credit finishes the book + its KDP package
    else if (book.is_preview) openPricing("One book credit unlocks the full book and its Amazon KDP package.", book.id);
    else unlockIncludedPackage();
  };
  const unlockIncludedPackage = async () => {  // full books (incl. legacy) include the KDP package
    try {
      await api.unlockPublish(book.id);
      setBook(await api.getBook(book.id));
      toast.success("Your Amazon KDP publishing package is included. Formats are ready!");
    } catch (e) {
      toast.error(apiError(e, "Could not open the publishing package."));
    }
  };

  const openSeriesList = () => { setActiveSeriesId(null); setView("series"); };
  const openSeriesDetail = (id) => { setActiveSeriesId(id); setView("series"); };
  const handleWriteNext = (seriesId) => {
    setPrefillSeriesId(seriesId);
    setView("home");
    toast.info("Series selected — enter the topic for the next book.");
  };

  const handleGenerate = async (payload) => {
    setBusy(true);
    // one key per submission attempt: a double-click or a resend after a dropped connection returns the same book
    submitKeyRef.current = submitKeyRef.current || crypto.randomUUID();
    try {
      const { id } = await api.generate({ ...payload, client_request_id: submitKeyRef.current });
      submitKeyRef.current = null;
      const b = await api.getBook(id);
      setBook(b);
      setBookUrl(id);
      setView("progress");
      startPolling(id);
    } catch (e) {
      setBusy(false);
      if (e?.response && e.response.status !== 409) submitKeyRef.current = null;  // nothing pending under this key
      if (e?.response?.status === 402) {
        openPricing(apiError(e, "You're out of free books and credits."));
      } else {
        toast.error(apiError(e, "Could not start generation. Please try again."));
      }
    }
  };

  const openBook = async (id) => {
    try {
      const b = await api.getBook(id);
      setBook(b);
      setBookUrl(id);
      setLibraryOpen(false);
      if (["queued", "researching", "outlining", "writing"].includes(b.status)) {
        setView("progress");  // still generating: show the live progress screen, polling the real job state
        setBusy(true);
        startPolling(id);
        return b;
      }
      setView("reader");
      // If a post-processing job (e.g. continuity check) is in progress, keep polling so the
      // reader updates live — and recovers automatically if the job turns out to be dead/stale.
      if (b.busy) pollBusy(id);
      return b;
    } catch (e) {
      setBookUrl(null);
      toast.error(e?.response?.status === 404 || e?.response?.status === 403
        ? "That book isn't in your library. If someone shared it with you, ask them for the share link."
        : "Could not open book.");
      return null;
    }
  };
  const openBookRef = useRef(null);
  openBookRef.current = openBook;
  const prevInfoRef = useRef(infoPage);
  useEffect(() => {  // keep the address bar in sync with info pages so links and refresh work
    if (infoPage) window.history.replaceState({}, "", `/${infoPage}`);
    else if (prevInfoRef.current) window.history.replaceState({}, "", "/");
    prevInfoRef.current = infoPage;
  }, [infoPage]);

  const pollBusy = (id) =>
    new Promise((resolve) => {
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const b = await api.getBook(id);
          setBook(b);
          if (!b.busy) {
            clearInterval(pollRef.current);
            resolve(b);
          }
        } catch (e) {
          /* keep polling */
        }
      }, 1500);
    });

  const onRegenCover = async () => {
    if (!book) return;
    try {
      await api.regenCover(book.id);
      toast.info("Rerolling the cover…");
      await pollBusy(book.id);
      toast.success("New cover ready");
      loadLibrary();
    } catch (e) {
      toast.error("Could not regenerate cover.");
    }
  };

  const onRegenChapter = async (number) => {
    if (!book) return;
    try {
      await api.regenChapter(book.id, number);
      toast.info(`Rerolling chapter ${number}…`);
      await pollBusy(book.id);
      toast.success(`Chapter ${number} rerolled`);
    } catch (e) {
      toast.error("Could not regenerate chapter.");
    }
  };

  const onRewriteChapter = async (number, instruction) => {
    if (!book) return;
    try {
      await api.rewriteChapter(book.id, number, instruction);
      toast.info("Rewording in a more natural voice…");
      await pollBusy(book.id);
      toast.success("Reworded");
    } catch (e) {
      toast.error("Could not reword chapter.");
    }
  };

  const onCoverOptions = async (style) => {
    if (!book) return;
    try {
      await api.coverOptions(book.id, style);
      toast.info(`Designing ${style} cover options…`);
      await pollBusy(book.id);
      toast.success("Cover options ready");
    } catch (e) {
      toast.error("Could not create cover options.");
    }
  };

  const onSelectCover = async (imageId) => {
    if (!book) return;
    try {
      const updated = await api.selectCover(book.id, imageId);
      setBook(updated);
      toast.success("Cover updated");
      loadLibrary();
    } catch (e) {
      toast.error("Could not set cover.");
    }
  };

  const onRestoreCover = async (versionId) => {
    if (!book) return;
    try {
      const updated = await api.restoreCover(book.id, versionId);
      setBook(updated);
      toast.success("Saved cover restored");
      loadLibrary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore that cover.");
    }
  };

  const onApplyDesign = async (index) => {
    if (!book) return;
    try {
      const updated = await api.applyCoverDesign(book.id, index);
      setBook(updated);
      toast.success("Cover design updated");
      loadLibrary();
    } catch (e) {
      toast.error("Could not apply that design.");
    }
  };

  const onUpdateBook = async (patch) => {
    if (!book) return;
    clearInterval(pollRef.current);
    try {
      const updated = await api.updateBook(book.id, patch);
      setBook(updated);
      toast.success("Changes saved");
      loadLibrary();
    } catch (e) {
      toast.error("Could not save changes.");
    }
  };

  const onGenerateMarketing = async () => {
    if (!book) return;
    try {
      toast.info("Writing your marketing kit…");
      const marketing = await api.marketingKit(book.id);
      setBook((b) => ({ ...b, marketing }));
      toast.success("Marketing kit ready");
    } catch (e) {
      toast.error(apiError(e, "Could not create marketing kit."));
    }
  };

  const onContinuityCheck = async () => {
    if (!book) return;
    try {
      await api.continuityCheck(book.id);
      toast.info("Running a continuity check across your book…");
      const b = await pollBusy(book.id);
      const flagged = b?.continuity_report?.flagged || 0;
      if (flagged > 0) toast.warning(`${flagged} section${flagged > 1 ? "s" : ""} flagged for review`);
      else toast.success("No continuity drift detected");
    } catch (e) {
      toast.error("Could not run continuity check.");
    }
  };

  const onCompleteBook = () => {
    if (!book) return;
    const entitled = user?.subscription_active || (user?.credits || 0) > 0 || user?.role === "admin";
    const alreadyPaid = book.gen_method && book.gen_method !== "preview";
    if (!entitled && !alreadyPaid) { openPricing("Unlock the full book: subscribe or buy a credit.", book.id); return; }
    if (alreadyPaid) { confirmCompleteBook(); return; }  // resuming a paid book never charges again
    setSpendOpen(true);
  };

  const confirmCompleteBook = async () => {
    setSpendOpen(false);
    if (!book) return;
    try {
      await api.completeBook(book.id);
      toast.info("Writing the rest of your book…");
      setBusy(true);
      setView("progress");
      startPolling(book.id);
    } catch (e) {
      if (e?.response?.status === 402) openPricing(apiError(e, "Unlock the full book."), book.id);
      else toast.error(apiError(e, "Could not unlock the full book."));
    }
  };

  const onDeleteChapter = async (number) => {
    if (!book) return;
    try {
      const updated = await api.deleteChapter(book.id, number);
      setBook(updated);
      toast.success(`Chapter ${number} deleted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete chapter.");
    }
  };

  const onAddChapter = async (afterNumber, title, focus) => {
    if (!book) return;
    try {
      await api.addChapter(book.id, afterNumber, title, focus);
      toast.info("Writing the new chapter…");
      await pollBusy(book.id);
      toast.success("New chapter added");
    } catch (e) {
      toast.error("Could not add chapter.");
    }
  };

  const onGenerateAudio = async (voice = "onyx", readAlong = false) => {
    if (!book) return;
    try {
      await api.startAudiobook(book.id, voice, readAlong);
      toast.info(readAlong ? "Recording your read-along…" : "Narrating your audiobook…");
      clearInterval(pollRef.current);
      await new Promise((resolve) => {
        pollRef.current = setInterval(async () => {
          try {
            const b = await api.getBook(book.id);
            setBook(b);
            if (b.audio_status === "ready" || b.audio_status === "error") {
              clearInterval(pollRef.current);
              resolve(b);
            }
          } catch (e) {
            /* keep polling */
          }
        }, 2000);
      });
      toast.success("Audiobook ready");
    } catch (e) {
      toast.error("Could not create audiobook.");
    }
  };

  const retryBook = async (id) => {
    try {
      await api.retryBook(id);
      const b = await api.getBook(id);
      setBook(b);
      setView("progress");
      setLibraryOpen(false);
      setBusy(true);
      startPolling(id);
      toast.info("Retrying generation…");
    } catch (e) {
      toast.error("Could not retry.");
    }
  };

  const deleteBook = async (id) => {
    try {
      await api.deleteBook(id);
      setLibrary((prev) => prev.filter((b) => b.id !== id));
      toast.success("Book deleted");
      if (book?.id === id) {
        setView("home");
        setBook(null);
      }
    } catch (e) {
      toast.error("Could not delete book.");
    }
  };

  const goHome = () => {
    setBookUrl(null);
    clearInterval(pollRef.current);
    setView("home");
    setBook(null);
    setBusy(false);
    setPrefillSeriesId("");
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    clearInterval(pollRef.current);
    setUser(null);
    setView("home");
    setBook(null);
    setLibrary([]);
    toast.success("Signed out");
  };

  // ---- render gates ----
  if (user === undefined) {
    return (
      <div className="min-h-screen grain bg-stone-950 flex items-center justify-center">
        <Toaster position="top-center" theme="dark"
          toastOptions={{ style: { background: "#0D1836", border: "1px solid #13234D", color: "#f5f5f4" } }} />
        <Loader2 className="w-8 h-8 text-steel-500 animate-spin" />
      </div>
    );
  }

  if (window.location.pathname.startsWith("/reset-password")) {
    return <ResetPasswordScreen onDone={() => { window.location.href = "/"; }} />;
  }

  if (notFound) {
    return <NotFound onHome={() => { window.history.replaceState({}, "", "/"); setNotFound(false); }} />;
  }

  // Info / legal pages — reachable whether logged in or out.
  if (infoPage) {
    const toaster = (
      <Toaster position="top-center" theme="dark"
        toastOptions={{ style: { background: "#0D1836", border: "1px solid #13234D", color: "#f5f5f4" } }} />
    );
    if (isMarketingPage(infoPage)) {
      const requireAuth = () => { setInfoPage(null); setAuthMode("register"); setShowAuth(true); };
      return (
        <>
          {toaster}
          <MarketingSite
            initialPage={infoPage}
            user={user}
            onHome={() => { setInfoPage(null); setView("home"); }}
            onGetStarted={user ? () => { setInfoPage(null); setView("home"); window.scrollTo({ top: 0 }); } : requireAuth}
            onSignIn={user ? null : () => { setInfoPage(null); setAuthMode("login"); setShowAuth(true); }}
            onCheckout={(key) => { if (!user) return requireAuth(); if (key) startCheckout(key); }}
            onPick={(s) => {
              if (!user) return requireAuth();
              setInfoPage(null);
              setSeed({ topic: s.topic || s.title || "", mode: s.mode || "nonfiction", n: Date.now() });
              setView("home");
              window.scrollTo({ top: 0 });
            }}
            onNavigateOther={(k) => setInfoPage(k)}
          />
        </>
      );
    }
    return (
      <>
        {toaster}
        <InfoPage
          initialPage={infoPage}
          onHome={() => setInfoPage(null)}
          onGetStarted={user ? null : () => { setInfoPage(null); setAuthMode("register"); setShowAuth(true); }}
          onNavigateOther={(k) => { window.history.pushState({}, "", `/${k}`); setInfoPage(k); window.scrollTo({ top: 0 }); }}
        />
      </>
    );
  }

  if (user === null && view === "shared" && book) {
    return (
      <div className="min-h-screen bg-[#050915] px-5" data-testid="shared-book-page">
        <Toaster position="top-center" theme="dark" />
        <SharedBook book={book} />
        <div className="max-w-2xl mx-auto pb-16 text-center">
          <button data-testid="shared-book-cta" onClick={() => { window.history.replaceState({}, "", "/"); setView("home"); setAuthMode("register"); setShowAuth(true); }}
            className="klyr-btn-metal font-ui text-sm font-semibold rounded-xl px-6 py-3 transition-[filter]">
            Create your own book with KLYR
          </button>
        </div>
      </div>
    );
  }

  if (user === null) {
    return (
      <>
        <Toaster position="top-center" theme="dark"
          toastOptions={{ style: { background: "#1c1917", border: "1px solid #292524", color: "#f5f5f4" } }} />
        {showAuth ? (
          <AuthScreen
            initialMode={authMode}
            onBack={() => setShowAuth(false)}
            onAuthed={(u) => { setShowAuth(false); setUser(u); }}
          />
        ) : (
          <LandingPage
            onGetStarted={() => { setAuthMode("register"); setShowAuth(true); }}
            onSignIn={() => { setAuthMode("login"); setShowAuth(true); }}
            onNavigate={setInfoPage}
          />
        )}
      </>
    );
  }

  return (
    <div className="App min-h-screen bg-[#050915]">
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{ style: { background: "#1c1917", border: "1px solid #292524", color: "#f5f5f4" } }}
      />
      {user && <ExportsTray bookId={view === "reader" ? book?.id : undefined} />}
      <Header
        onHome={goHome}
        onOpenLibrary={() => {
          loadLibrary();
          setLibraryOpen(true);
        }}
        onOpenSeries={() => { loadSeries(); openSeriesList(); }}
        onOpenAdmin={() => setView("admin")}
        libraryCount={library.length}
        user={user}
        onUpgrade={() => openPricing("")}
        onAccount={() => { setBookUrl(null); setView("billing"); }}
        onLogout={logout}
      />

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-6">
        {view === "home" && (
          <>
            <CreatorForm
              onGenerate={handleGenerate}
              busy={busy}
              admin={user?.role === "admin"}
              entitled={user?.subscription_active || (user?.credits || 0) > 0 || user?.role === "admin"}
              seriesList={series}
              initialSeriesId={prefillSeriesId}
              seed={seed}
              onCreateSeries={() => setSeriesModalOpen(true)}
              onBuySeries={handleBuySeries}
              onManageSeries={() => { loadSeries(); openSeriesList(); }}
            />
            <MarketResearch onPick={(s) => {
              setSeed({ topic: s.topic || s.title || "", mode: s.mode || "nonfiction", n: Date.now() });
              window.scrollTo({ top: 0, behavior: "smooth" });
              toast.success("Idea loaded into the creator above");
            }} />
          </>
        )}
        {view === "progress" && <ProgressPanel book={book} onOpen={() => setView("showcase")} />}
        {view === "showcase" && book && (
          <BookShowcase book={book} onOpen={() => setView("reader")} onBack={() => { setView("home"); setLibraryOpen(true); }} onBookChange={setBook} />
        )}
        {view === "billing" && <BillingPage user={user} onBack={goHome} onUpgrade={() => openPricing("")} onRefreshUser={refreshUser} />}
        {view === "admin" && <AdminPanel onBack={goHome} />}
        {view === "series" && (
          <SeriesPage
            seriesList={series}
            activeSeriesId={activeSeriesId}
            onSelectSeries={openSeriesDetail}
            onBack={() => setActiveSeriesId(null)}
            onNewSeries={() => setSeriesModalOpen(true)}
            onBuySeries={handleBuySeries}
            onOpenBook={openBook}
            onWriteNext={handleWriteNext}
            onRefresh={loadSeries}
          />
        )}
        {view === "shared" && book && <SharedBook book={book} />}
        {view === "reader" && book && (
          <BookReader
            book={book}
            user={user}
            onBuyPublish={handleBuyPublish}
            onBack={goHome}
            onRegenChapter={onRegenChapter}
            onRegenCover={onRegenCover}
            onUpdateBook={onUpdateBook}
            onBookReplaced={setBook}
            onAddChapter={onAddChapter}
            onDeleteChapter={onDeleteChapter}
            onGenerateAudio={onGenerateAudio}
            onRewriteChapter={onRewriteChapter}
            onCoverOptions={onCoverOptions}
            onSelectCover={onSelectCover}
            onApplyDesign={onApplyDesign}
            onRestoreCover={onRestoreCover}
            onGenerateMarketing={onGenerateMarketing}
            onContinuityCheck={onContinuityCheck}
            onCompleteBook={onCompleteBook}
            onShowcase={() => setView("showcase")}
          />
        )}
      </main>

      <SiteFooter onNavigate={setInfoPage} />

      <RecentBooks
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        books={library}
        onOpen={openBook}
        onDelete={deleteBook}
        onRetry={retryBook}
      />

      <PricingModal open={pricingOpen} onOpenChange={setPricingOpen} reason={pricingReason} bookId={pricingBookId} />
      <SpendCreditDialog open={spendOpen} onOpenChange={setSpendOpen} user={user} book={book} onConfirm={confirmCompleteBook} />
      <SeriesModal
        open={seriesModalOpen}
        onOpenChange={setSeriesModalOpen}
        onSubmit={handleCreateSeries}
        subscribed={!!user.subscription_active}
      />

      {paying && (
        <div className="fixed inset-0 z-[100] bg-stone-950/80 backdrop-blur flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-10 h-10 text-steel-500 animate-spin" />
          <p className="font-ui text-sm text-stone-300">Confirming your payment…</p>
        </div>
      )}
    </div>
  );
}

export default App;
